import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import type { BasePagination } from "../../../types/api.ts"
import { getAttachments } from "../../Album/api/attachment.services.ts"
import type { AttachmentListData } from "../../Album/api/services.types.ts"
import {
  mapAttachmentToFileItem,
  mapDocumentToFileItem
} from "../../Files/helpers/map-to-file-item.ts"
import type { FileItem, FileRights } from "../../Files/types/file-item.types.ts"
import { getProjectDocuments } from "../../Project/api/project-documents.services.ts"
import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"

const PAGE_SIZE = 25

/** Global Trash — every trashed document and photo the caller may manage,
 * company-wide. A narrowed sibling of `useGlobalFilesData` (same two-store
 * paged-fetch shape), not the project-scoped `useTrash`: no `attention`
 * (Document Health doesn't apply to trashed rows), and sorted by
 * `deletedAt` descending rather than by the Files sort control, mirroring
 * `useTrash`'s own "most recently trashed first" convention. */
export function useGlobalTrash(search: string) {
  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)
  const moduleDocs = useAppSelector(
    (state) => state.auth?.permissions?.codes?.projects?.docs
  )
  const rights: FileRights = useMemo(() => {
    const editAllowed = !!moduleDocs?.project_doc_edit
    const deleteAllowed = !!moduleDocs?.project_doc_delete
    const manageOthers = !!moduleDocs?.project_doc_manage_others
    return {
      currentUserId: currentUserId ?? "",
      editAllowed,
      editForeignAllowed: editAllowed && manageOthers,
      deleteAllowed,
      deleteForeignAllowed: deleteAllowed && manageOthers,
      manageOthersAllowed: manageOthers
    }
  }, [moduleDocs, currentUserId])

  const [documents, setDocuments] = useState<FileItem[]>([])
  const [photos, setPhotos] = useState<FileItem[]>([])
  const [documentsDone, setDocumentsDone] = useState(false)
  const [photosDone, setPhotosDone] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [failed, setFailed] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const pageRef = useRef(1)
  const requestIdRef = useRef(0)

  const fetchPage = useCallback(
    async (page: number, append: boolean) => {
      const requestId = ++requestIdRef.current
      if (append) setLoadingMore(true)
      else setLoading(true)

      const [documentsResponse, photosResponse] = await Promise.all([
        getProjectDocuments({
          trashed: true,
          page,
          page_size: PAGE_SIZE,
          q: search || undefined
        }),
        getAttachments({
          trashed: true,
          page,
          page_size: PAGE_SIZE,
          q: search || undefined
        })
      ])

      if (requestId !== requestIdRef.current) return

      if (!documentsResponse.ok || !photosResponse.ok) {
        setFailed(true)
        setLoading(false)
        setLoadingMore(false)
        setHasLoaded(true)
        return
      }
      setFailed(false)

      const documentsData = documentsResponse.data as
        | BasePagination<ProjectDocument>
        | undefined
      const photosData = photosResponse.data as AttachmentListData | undefined
      const documentRows = documentsData?.results ?? []
      const photoRows = photosData
        ? Array.isArray(photosData)
          ? photosData
          : photosData.results
        : []

      const mappedDocuments = documentRows.map((d) =>
        mapDocumentToFileItem(d, rights)
      )
      const mappedPhotos = photoRows.map((p) =>
        mapAttachmentToFileItem(p, rights)
      )

      setDocuments((prev) =>
        append ? [...prev, ...mappedDocuments] : mappedDocuments
      )
      setPhotos((prev) => (append ? [...prev, ...mappedPhotos] : mappedPhotos))
      setDocumentsDone(!documentsData?.next)
      setPhotosDone(
        photosData
          ? Array.isArray(photosData)
            ? true
            : !photosData.next
          : true
      )
      setLoading(false)
      setLoadingMore(false)
      setHasLoaded(true)
    },
    [search, rights]
  )

  useEffect(() => {
    pageRef.current = 1
    void fetchPage(1, false)
  }, [fetchPage])

  const items = useMemo(() => {
    const combined = [...documents, ...photos]
    // Most-recently-trashed first, matching the per-project `useTrash`.
    return [...combined].sort((a, b) =>
      (a.deletedAt ?? "") < (b.deletedAt ?? "") ? 1 : -1
    )
  }, [documents, photos])

  const hasMore = !documentsDone || !photosDone

  const loadMore = () => {
    if (loadingMore || !hasMore) return
    pageRef.current += 1
    void fetchPage(pageRef.current, true)
  }

  const reload = useCallback(() => {
    pageRef.current = 1
    return fetchPage(1, false)
  }, [fetchPage])

  return {
    items,
    loading,
    loadingMore,
    failed,
    hasMore,
    loadMore,
    reload,
    rights,
    isEmpty: hasLoaded && items.length === 0
  }
}
